/**
 * Copyright © 2026 Delicious AI, LLC
 */

import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd'
import { DragIndicator } from '@mui/icons-material'
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React from 'react'

import useT from '@/i18n/useT'
import { LocalizationHelpers } from '@/utils/localization.helpers'

import type { DisplayRow } from './display-table.types'

type Props = {
  rows: DisplayRow[]
  onReorder: (rows: DisplayRow[]) => void
  loading?: boolean
  error?: string | null
}

const reorderRows = (
  rows: DisplayRow[],
  sourceIndex: number,
  destinationIndex: number,
): DisplayRow[] => {
  const next = [...rows]
  const [removed] = next.splice(sourceIndex, 1)
  next.splice(destinationIndex, 0, removed)
  return next.map((row, index) => ({
    ...row,
    sortOrder: index,
  }))
}

export const DisplayTableReorder: React.FC<Props> = ({
  rows,
  onReorder,
  loading,
  error,
}) => {
  const { t } = useT()

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    if (result.source.index === result.destination.index) return
    onReorder(reorderRows(rows, result.source.index, result.destination.index))
  }

  const headCells = [
    { id: 'drag', label: '' },
    { id: 'image', label: t('displaysTable.imageHeader') },
    { id: 'name', label: t('displaysTable.nameHeader') },
    { id: 'order', label: t('displaysTable.orderHeader') },
    {
      id: 'masterCategory',
      label: t('displaysTable.masterCategoryHeader'),
    },
    { id: 'actions', label: '' },
  ]

  return (
    <Paper sx={{ width: '100%' }}>
      <Box sx={{ pl: 2, pr: 1, pt: 2, pb: 1 }}>
        <Typography variant="h6">
          {t('displaysTable.reorderTitle', 'Reorder displays')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            'displaysTable.reorderHint',
            'Drag rows to change display order, then click Done to save.',
          )}
        </Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          {error}
        </Alert>
      )}
      <TableContainer>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Table size="medium">
            <TableHead>
              <TableRow>
                {headCells.map(cell => (
                  <TableCell key={cell.id} align="left">
                    {cell.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <Droppable droppableId="display-reorder-list">
              {provided => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={headCells.length} align="center">
                        <CircularProgress size={32} sx={{ my: 4 }} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((display, index) => (
                      <Draggable
                        key={display.id}
                        draggableId={display.id}
                        index={index}
                      >
                        {(dragProvided, snapshot) => (
                          <TableRow
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            sx={{
                              bgcolor: snapshot.isDragging
                                ? 'action.hover'
                                : undefined,
                            }}
                          >
                            <TableCell
                              align="left"
                              {...dragProvided.dragHandleProps}
                              sx={{ width: 48, cursor: 'grab' }}
                            >
                              <DragIndicator color="action" />
                            </TableCell>
                            <TableCell align="left">
                              {display.imageUrl ? (
                                <img
                                  src={display.imageUrl}
                                  alt={display.name}
                                  style={{
                                    height: 175,
                                    objectFit: 'contain',
                                  }}
                                />
                              ) : null}
                            </TableCell>
                            <TableCell align="left">
                              <Typography>{display.name}</Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Typography>
                                {LocalizationHelpers.ordinalize(
                                  t,
                                  display.sortOrder + 1,
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Typography>{display.masterCategory}</Typography>
                            </TableCell>
                            <TableCell align="right" />
                          </TableRow>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>
      </TableContainer>
    </Paper>
  )
}
